/**
 * 初回利用時のチュートリアル表示
 */
export class TutorialOverlay {
  private container: HTMLElement
  private currentStep: number = 0
  private onCompleteCallback?: () => void
  
  private steps = [
    {
      target: '.header__title',
      title: 'C言語ループ可視化ツールへようこそ！',
      content: 'このツールでは、C言語のループ（for、while、do-while）の動作を視覚的に理解できます。',
      position: 'bottom'
    },
    {
      target: '#monaco-editor',
      title: 'コードエディター',
      content: 'ここにC言語のコードを入力します。for文、while文、do-while文を含むコードを書いてみましょう。',
      position: 'right'
    },
    {
      target: '.sample-button',
      title: 'サンプルコード',
      content: 'サンプルボタンをクリックすると、様々な難易度のサンプルコードを選択できます。初めての方は「初級」から始めることをお勧めします。',
      position: 'bottom'
    },
    {
      target: '#run-btn',
      title: '実行ボタン',
      content: 'コードを書いたら、実行ボタン（またはCtrl+Enter）で実行を開始します。',
      position: 'bottom'
    },
    {
      target: '#step-btn',
      title: 'ステップ実行',
      content: 'ステップボタン（またはCtrl+.）で、コードを1行ずつ実行できます。ループの動作を詳しく観察するのに便利です。',
      position: 'bottom'
    },
    {
      target: '.speed-control',
      title: '実行速度の調整',
      content: 'スライダーで実行速度を調整できます。ゆっくり実行したい場合は左に、速く実行したい場合は右に動かしてください。',
      position: 'bottom'
    },
    {
      target: '.variables-panel',
      title: '変数パネル',
      content: '実行中の変数の値がリアルタイムで表示されます。ループ変数は青色でハイライトされます。',
      position: 'left'
    },
    {
      target: '.loop-condition-panel',
      title: 'ループ条件パネル',
      content: 'ループの条件式とその評価結果が表示されます。条件がtrueなら継続、falseなら終了します。',
      position: 'left'
    },
    {
      target: '#loop-visualizer',
      title: 'ループフロー可視化',
      content: 'ループの流れが視覚的に表示されます。ネストしたループも階層的に表現されます。',
      position: 'top'
    }
  ]
  
  constructor() {
    this.container = this.createContainer()
    this.setupStyles()
  }
  
  /**
   * コンテナを作成
   */
  private createContainer(): HTMLElement {
    const container = document.createElement('div')
    container.className = 'tutorial-overlay'
    document.body.appendChild(container)
    return container
  }
  
  /**
   * スタイルを設定
   */
  private setupStyles(): void {
    const style = document.createElement('style')
    style.textContent = `
      .tutorial-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9999;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.3s ease;
      }
      
      .tutorial-overlay.active {
        opacity: 1;
        pointer-events: auto;
      }
      
      .tutorial-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.7);
      }
      
      .tutorial-highlight {
        position: absolute;
        border: 3px solid var(--primary-color);
        border-radius: 0.5rem;
        box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7);
        transition: all 0.3s ease;
        animation: tutorialPulse 2s ease-in-out infinite;
      }
      
      @keyframes tutorialPulse {
        0%, 100% {
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7);
        }
        50% {
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7), 
                      0 0 20px 5px rgba(37, 99, 235, 0.5);
        }
      }
      
      .tutorial-tooltip {
        position: absolute;
        background: white;
        border-radius: 0.5rem;
        padding: 1.5rem;
        max-width: 400px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
        animation: tooltipSlide 0.3s ease-out;
      }
      
      @keyframes tooltipSlide {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .tutorial-tooltip::before {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        border-style: solid;
      }
      
      .tutorial-tooltip.position-bottom::before {
        top: -8px;
        left: 50%;
        transform: translateX(-50%);
        border-width: 0 8px 8px 8px;
        border-color: transparent transparent white transparent;
      }
      
      .tutorial-tooltip.position-top::before {
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        border-width: 8px 8px 0 8px;
        border-color: white transparent transparent transparent;
      }
      
      .tutorial-tooltip.position-left::before {
        right: -8px;
        top: 50%;
        transform: translateY(-50%);
        border-width: 8px 0 8px 8px;
        border-color: transparent transparent transparent white;
      }
      
      .tutorial-tooltip.position-right::before {
        left: -8px;
        top: 50%;
        transform: translateY(-50%);
        border-width: 8px 8px 8px 0;
        border-color: transparent white transparent transparent;
      }
      
      .tutorial-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 0.75rem;
      }
      
      .tutorial-content {
        font-size: 0.875rem;
        color: var(--text-secondary);
        line-height: 1.5;
        margin-bottom: 1rem;
      }
      
      .tutorial-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      
      .tutorial-progress {
        display: flex;
        gap: 0.375rem;
      }
      
      .tutorial-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #e5e7eb;
        transition: all 0.3s ease;
      }
      
      .tutorial-dot.active {
        background: var(--primary-color);
        transform: scale(1.25);
      }
      
      .tutorial-buttons {
        display: flex;
        gap: 0.5rem;
      }
      
      .tutorial-btn {
        padding: 0.5rem 1rem;
        border: none;
        border-radius: 0.375rem;
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
      }
      
      .tutorial-btn--skip {
        background: #f3f4f6;
        color: #6b7280;
      }
      
      .tutorial-btn--skip:hover {
        background: #e5e7eb;
      }
      
      .tutorial-btn--next {
        background: var(--primary-color);
        color: white;
      }
      
      .tutorial-btn--next:hover {
        background: #1d4ed8;
        transform: translateY(-1px);
      }
      
      .tutorial-btn--complete {
        background: var(--secondary-color);
        color: white;
      }
      
      .tutorial-btn--complete:hover {
        background: #059669;
        transform: translateY(-1px);
      }
      
      @media (max-width: 768px) {
        .tutorial-tooltip {
          max-width: calc(100vw - 2rem);
          margin: 1rem;
        }
      }
    `
    document.head.appendChild(style)
  }
  
  /**
   * チュートリアルを開始
   */
  start(): void {
    this.currentStep = 0
    this.render()
    this.container.classList.add('active')
    
    // localStorageに表示済みフラグを保存
    localStorage.setItem('tutorial-shown', 'true')
  }
  
  /**
   * レンダリング
   */
  private render(): void {
    const step = this.steps[this.currentStep]
    const target = document.querySelector(step.target)
    
    if (!target) {
      this.next()
      return
    }
    
    const rect = target.getBoundingClientRect()
    const isLastStep = this.currentStep === this.steps.length - 1
    
    this.container.innerHTML = `
      <div class="tutorial-backdrop"></div>
      <div class="tutorial-highlight" style="
        top: ${rect.top - 5}px;
        left: ${rect.left - 5}px;
        width: ${rect.width + 10}px;
        height: ${rect.height + 10}px;
      "></div>
      <div class="tutorial-tooltip position-${step.position}" style="${this.getTooltipPosition(rect, step.position)}">
        <div class="tutorial-title">${step.title}</div>
        <div class="tutorial-content">${step.content}</div>
        <div class="tutorial-actions">
          <div class="tutorial-progress">
            ${this.steps.map((_, i) => `
              <div class="tutorial-dot ${i === this.currentStep ? 'active' : ''}"></div>
            `).join('')}
          </div>
          <div class="tutorial-buttons">
            <button class="tutorial-btn tutorial-btn--skip">スキップ</button>
            ${isLastStep ? `
              <button class="tutorial-btn tutorial-btn--complete">完了</button>
            ` : `
              <button class="tutorial-btn tutorial-btn--next">次へ</button>
            `}
          </div>
        </div>
      </div>
    `
    
    // イベントリスナー
    const skipBtn = this.container.querySelector('.tutorial-btn--skip')
    skipBtn?.addEventListener('click', () => this.skip())
    
    const nextBtn = this.container.querySelector('.tutorial-btn--next')
    nextBtn?.addEventListener('click', () => this.next())
    
    const completeBtn = this.container.querySelector('.tutorial-btn--complete')
    completeBtn?.addEventListener('click', () => this.complete())
  }
  
  /**
   * ツールチップの位置を計算
   */
  private getTooltipPosition(targetRect: DOMRect, position: string): string {
    const tooltipWidth = 400
    const tooltipHeight = 200
    const margin = 20
    
    let top = 0
    let left = 0
    
    switch (position) {
      case 'bottom':
        top = targetRect.bottom + margin
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2
        break
      case 'top':
        top = targetRect.top - tooltipHeight - margin
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2
        break
      case 'left':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2
        left = targetRect.left - tooltipWidth - margin
        break
      case 'right':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2
        left = targetRect.right + margin
        break
    }
    
    // 画面外にはみ出ないように調整
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    
    if (left < margin) left = margin
    if (left + tooltipWidth > viewportWidth - margin) {
      left = viewportWidth - tooltipWidth - margin
    }
    
    if (top < margin) top = margin
    if (top + tooltipHeight > viewportHeight - margin) {
      top = viewportHeight - tooltipHeight - margin
    }
    
    return `top: ${top}px; left: ${left}px;`
  }
  
  /**
   * 次のステップへ
   */
  private next(): void {
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++
      this.render()
    }
  }
  
  /**
   * スキップ
   */
  private skip(): void {
    this.container.classList.remove('active')
    if (this.onCompleteCallback) {
      this.onCompleteCallback()
    }
  }
  
  /**
   * 完了
   */
  private complete(): void {
    this.container.classList.remove('active')
    if (this.onCompleteCallback) {
      this.onCompleteCallback()
    }
  }
  
  /**
   * 完了時のコールバックを設定
   */
  setOnCompleteCallback(callback: () => void): void {
    this.onCompleteCallback = callback
  }
  
  /**
   * チュートリアルが表示済みかチェック
   */
  static hasBeenShown(): boolean {
    return localStorage.getItem('tutorial-shown') === 'true'
  }
  
  /**
   * チュートリアルをリセット
   */
  static reset(): void {
    localStorage.removeItem('tutorial-shown')
  }
}